import React, { useState, useEffect, useMemo } from 'react';
import { Card, Input, Select, DatePicker, Row, Col, Typography, Empty, Badge, Button, message } from 'antd';
import { CalendarOutlined, SearchOutlined, CopyOutlined } from '@ant-design/icons';
import moment from 'moment';
import { fetchEventsForCalendar } from '../../server/publications/publicationServer';
import { getAllEnableCities } from '../../server/countries/countriesServer';
import { getActiveCountries } from '../../server/configuration/FiltersServer';
import Loader from '../../components/Loading';
import EventCard from '../../components/EventCard';
import axios from 'axios';

const { Option } = Select;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const EventCalendar = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cities, setCities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState([moment('2025-07-01'), moment('2025-09-30')]);

  // Load initial data
  useEffect(() => {
    loadCountriesAndCities();
    loadEvents();
  }, []);

  // Load events when filters change
  useEffect(() => {
    loadEvents();
  }, [selectedCity, selectedCountry, searchTerm, dateRange]);

  const loadCountriesAndCities = async () => {
    try {
      const [countriesResponse, citiesResponse] = await Promise.all([
        getActiveCountries(),
        getAllEnableCities()
      ]);
      
      if (countriesResponse?.data) {
        setCountries(countriesResponse.data);
      }
      
      if (citiesResponse?.data) {
        setCities(citiesResponse.data);
      }
    } catch (error) {
      console.error('Error loading countries/cities:', error);
    }
  };

  const loadEvents = async () => {
    try {
      setLoading(true);
      const options = {
        city: selectedCity,
        country: selectedCountry,
        search: searchTerm,
        startDate: dateRange[0].toISOString(),
        endDate: dateRange[1].toISOString(),
      };

      const response = await fetchEventsForCalendar(options);
      // The API returns events in response.data array (fetchEventsForCalendar returns response.data)
      const eventsData = response?.data || [];
      console.log('📅 [EventCalendar] Raw API response:', response);
      console.log('📅 [EventCalendar] Extracted events data:', eventsData.length, eventsData);
      setEvents(eventsData);
    } catch (error) {
      console.error('Error loading events:', error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCountryChange = (countryId) => {
    setSelectedCountry(countryId);
    setSelectedCity(null); // Reset city when country changes
  };

  const handleCityChange = (cityId) => {
    setSelectedCity(cityId);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setDateRange(dates);
    }
  };

  // Group events by date
  const eventsByDate = useMemo(() => {
    console.log('🗓️ [EventCalendar] Grouping events by date, total events:', events.length);
    const grouped = {};
    
    events.forEach((event, index) => {
      const eventData = event.publicationData || event;
      console.log(`🗓️ [Event ${index}] Processing:`, {
        title: eventData.title,
        startTime: eventData.startTime,
        eventData: eventData
      });
      
      const eventDate = moment(eventData.startTime);
      const dateKey = eventDate.format('YYYY-MM-DD');
      console.log(`🗓️ [Event ${index}] Date key:`, dateKey, 'from startTime:', eventData.startTime);
      
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(event);
    });

    console.log('🗓️ [EventCalendar] Grouped events:', grouped);

    // Sort events within each date by time
    Object.keys(grouped).forEach(date => {
      grouped[date].sort((a, b) => {
        const aData = a.publicationData || a;
        const bData = b.publicationData || b;
        return moment(aData.startTime) - moment(bData.startTime);
      });
    });

    return grouped;
  }, [events]);

  // Get sorted dates
  const sortedDates = Object.keys(eventsByDate).sort();
  console.log('📅 [EventCalendar] Sorted dates:', sortedDates);
  console.log('📅 [EventCalendar] Events by date:', eventsByDate);

  // Get city name by ID
  const getCityName = (cityId) => {
    const city = cities.find(c => c._id === cityId);
    return city ? city.name : 'Unknown City';
  };

  // Get country name by ID
  const getCountryName = (countryId) => {
    const country = countries.find(c => c._id === countryId);
    return country ? country.name : 'Unknown Country';
  };

  // Filter cities by selected country
  const filteredCities = useMemo(() => {
    if (!selectedCountry) return cities;
    const country = countries.find(c => c._id === selectedCountry);
    return country?.cities || [];
  }, [selectedCountry, countries, cities]);

  // Function to fetch this week's events for podcast
  const fetchThisWeekEventsForPodcast = async () => {
    try {
      setLoading(true);
      message.loading({ content: 'Fetching this week\'s events...', key: 'podcast' });

      // Hardcoded Montreal city ID from the API URL you provided
      const montrealCityId = '667eb6e09333d9ccabffba9d';

      const response = await axios.get('https://mysway.app/api/publications/discovery', {
        params: {
          pagination: true,
          page: 1,
          limit: 50, // Get more events to ensure we have them all
          publicationType: 'Event',
          'condition[isPublished]': true,
          optimized: false, // Set to false to get full event data including descriptions
          timezone: 'America/Montreal',
          timeframe: 'this_week',
          filterLocation: montrealCityId
        }
      });

      const events = response.data?.data || [];

      if (events.length === 0) {
        message.warning({ content: 'No events found for this week', key: 'podcast' });
        return;
      }

      // Format events for podcast
      const podcastText = formatEventsForPodcast(events);

      // Copy to clipboard
      await navigator.clipboard.writeText(podcastText);
      message.success({ content: `Copied ${events.length} events to clipboard!`, key: 'podcast' });

    } catch (error) {
      console.error('Error fetching events for podcast:', error);
      message.error({ content: 'Failed to fetch events', key: 'podcast' });
    } finally {
      setLoading(false);
    }
  };

  // Format events for podcast copy-paste
  const formatEventsForPodcast = (events) => {
    // Group events by date
    const eventsByDate = {};

    events.forEach(event => {
      const eventData = event.publicationData || event;
      const eventDate = moment(eventData.startTime);
      const dateKey = eventDate.format('YYYY-MM-DD');

      if (!eventsByDate[dateKey]) {
        eventsByDate[dateKey] = [];
      }
      eventsByDate[dateKey].push(eventData);
    });

    // Sort dates
    const sortedDates = Object.keys(eventsByDate).sort();

    // Build formatted text
    let text = '==============================================\n';
    text += '🎵 THIS WEEK\'S EVENTS IN MONTREAL 🎵\n';
    text += `Generated on: ${moment().format('MMMM Do, YYYY')}\n`;
    text += `Total Events: ${events.length}\n`;
    text += '==============================================\n\n';

    sortedDates.forEach(date => {
      const dayEvents = eventsByDate[date];
      const dateObj = moment(date);

      text += `\n📅 ${dateObj.format('dddd, MMMM Do, YYYY').toUpperCase()}\n`;
      text += `${'='.repeat(50)}\n\n`;

      dayEvents.forEach((event, index) => {
        const startTime = moment(event.startTime).format('h:mm A');
        const venue = event.organisationName || 'Venue TBA';
        const address = event.localisation?.address || 'Address TBA';

        text += `${index + 1}. ${event.title}\n`;
        text += `   🕐 Time: ${startTime}\n`;
        text += `   📍 Venue: ${venue}\n`;
        text += `   📌 Address: ${address}\n`;

        if (event.description) {
          text += `   📝 Description: ${event.description}\n`;
        }

        if (event.flyer) {
          text += `   🎨 Flyer: ${event.flyer}\n`;
        }

        text += '\n';
      });
    });

    text += '\n==============================================\n';
    text += '🎙️ Ready for your podcast! 🎙️\n';
    text += '==============================================\n';

    return text;
  };



  const DateSection = ({ date, events }) => (
    <Card 
      className="mb-4" 
      title={
        <div className="flex items-center space-x-2">
          <CalendarOutlined className="text-blue-500" />
          <span className="text-lg font-semibold">
            {moment(date).format('dddd, MMMM Do, YYYY')}
          </span>
          <Badge count={events.length} className="ml-2" />
        </div>
      }
    >
      <div className="space-y-2">
        {events.map(event => (
          <EventCard key={event._id} event={event} />
        ))}
      </div>
    </Card>
  );

  return (
    <div className="event-calendar">
      <Card className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <Title level={3} className="mb-0">
            <CalendarOutlined className="mr-2" />
            Events Calendar
          </Title>

          <Button
            type="primary"
            icon={<CopyOutlined />}
            onClick={fetchThisWeekEventsForPodcast}
            size="large"
            className="bg-blue-600 hover:bg-blue-700"
          >
            Copy This Week's Events for Podcast
          </Button>
        </div>

        <Row gutter={[16, 16]} className="mb-4">
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Search events..."
              value={searchTerm}
              onChange={handleSearchChange}
              prefix={<SearchOutlined />}
              allowClear
            />
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Select Country"
              value={selectedCountry}
              onChange={handleCountryChange}
              className="w-full"
              allowClear
            >
              {countries.map(country => (
                <Option key={country._id} value={country._id}>
                  {country.name}
                </Option>
              ))}
            </Select>
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Select City"
              value={selectedCity}
              onChange={handleCityChange}
              className="w-full"
              allowClear
              disabled={!selectedCountry}
            >
              {filteredCities.map(city => (
                <Option key={city._id} value={city._id}>
                  {city.name}
                </Option>
              ))}
            </Select>
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <RangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
              className="w-full"
              format="YYYY-MM-DD"
            />
          </Col>
        </Row>
        
        <div className="text-sm text-gray-600 mb-2">
          <Text>
            Showing {events.length} events
            {selectedCity && ` in ${getCityName(selectedCity)}`}
            {selectedCountry && !selectedCity && ` in ${getCountryName(selectedCountry)}`}
            {searchTerm && ` matching "${searchTerm}"`}
          </Text>
        </div>
        
        {/* Debug info */}
        {events.length > 0 && (
          <div className="text-xs text-gray-500 mb-2 p-2 bg-gray-50 rounded">
            <strong>Debug:</strong> Found {events.length} events, grouped into {sortedDates.length} dates: {sortedDates.join(', ')}
          </div>
        )}
      </Card>

      {loading ? (
        <Loader />
      ) : sortedDates.length === 0 ? (
        <Card>
          <Empty 
            description="No events found" 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      ) : (
        <div className="space-y-4">
          {sortedDates.map(date => (
            <DateSection 
              key={date} 
              date={date} 
              events={eventsByDate[date]} 
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default EventCalendar; 